import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';
import { User } from '../../types';

export const AdminClients: React.FC = () => {
  const [clients, setClients] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const loadClients = async () => {
    setIsLoading(true);
    try {
      const data = await adminService.listClients({ search: search || undefined });
      setClients(data.results ?? []);
      setTotal(data.count ?? 0);
    } catch {
      setClients([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, [search]);

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Clients</h1>
          <p style={s.subtitle}>{total} total clients registered</p>
        </div>
        <input
          style={s.search}
          placeholder="Search client name, email, phone..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {isLoading ? (
        <div style={s.center}><div style={s.spinner} /></div>
      ) : clients.length === 0 ? (
        <div style={s.empty}>No clients found.</div>
      ) : (
        <div style={s.tableWrap}>
          <div style={s.table}>
            <div style={s.thead}>
              {['Client', 'Email', 'Phone', 'Status', 'Joined'].map((label) => (
                <div key={label} style={s.th}>{label}</div>
              ))}
            </div>
            {clients.map((client) => (
              <div key={client.id} style={s.row}>
                <div style={s.td}>
                  <strong>{client.first_name} {client.last_name}</strong>
                </div>
                <div style={s.td}>{client.email || '—'}</div>
                <div style={s.td}>{client.phone || '—'}</div>
                <div style={s.td}>
                  <span style={{
                    ...s.badge,
                    background: client.is_verified ? 'var(--sc-success-bg)' : 'var(--sc-warning-bg)',
                    color: client.is_verified ? 'var(--sc-success-text)' : 'var(--sc-warning-text)',
                  }}>
                    {client.is_verified ? 'Verified' : 'Unverified'}
                  </span>
                </div>
                <div style={s.td}>{new Date(client.created_at).toLocaleDateString('en-ZW')}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', fontFamily: "'Outfit', sans-serif" },
  subtitle: { fontSize: 14, color: 'var(--sc-text-secondary)', margin: 0 },
  search: { padding: '10px 14px', border: '1px solid var(--sc-border)', borderRadius: 10, fontSize: 14, width: 300, outline: 'none', background: 'var(--sc-bg-secondary)', color: 'var(--sc-text-primary)' },
  center: { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  empty: { textAlign: 'center', padding: 60, color: 'var(--sc-text-secondary)', background: 'var(--sc-bg-secondary)', borderRadius: 14, border: '1px solid var(--sc-border)' },
  tableWrap: { overflowX: 'auto', borderRadius: 14, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-sm)' },
  table: { minWidth: 760, background: 'var(--sc-bg-secondary)' },
  thead: { display: 'grid', gridTemplateColumns: '1.3fr 1.6fr 1fr 0.9fr 0.9fr', padding: '10px 18px', background: 'var(--sc-bg-primary)', borderBottom: '1px solid var(--sc-border)' },
  th: { fontSize: 11, fontWeight: 800, color: 'var(--sc-text-secondary)', textTransform: 'uppercase' },
  row: { display: 'grid', gridTemplateColumns: '1.3fr 1.6fr 1fr 0.9fr 0.9fr', padding: '14px 18px', borderBottom: '1px solid var(--sc-border)', alignItems: 'center' },
  td: { fontSize: 13, color: 'var(--sc-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  badge: { display: 'inline-block', padding: '4px 9px', borderRadius: 7, fontSize: 11, fontWeight: 800 },
};
